import React, { useContext, useEffect, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import * as LeaveServices from "../../Services/LeaveServices";
import { UserContext } from "../../Context/UserContext";
import Calendar from "./Calendar";
import { Common } from "../../Components";
import {
  LEAVE_CALENDAR_COLUMN_LABELS,
  LEAVE_CALENDAR_TABLE_COLUMNS,
  TOAST_MESSAGES,
  transformLeaveData,
} from "./constants";
import { formatDateDDMMYYYY } from "../../Utils";
import PopupForm from "./Components/PopupForm";
import { ROLES } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const LeaveCalendar = () => {
  const [leaveCalendarList, setLeaveCalendarList] = useState([]);
  const [events, setEvents] = useState([]);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [pagination, setPagination] = useState({
    count: 0,
    offset: 0,
    limit: 10,
  });

  const [isPaginationReset, setIsPaginationReset] = useState(false);
  const [showPopUpForm, setShowPopUpForm] = useState(false);

  const { user } = useContext(UserContext);

  // Default Form
  const addLeaveCalendarForm = () => ({
    date: "",
    description: "",
    note: "",
  });

  // Fetch Leave List
  const getLeaveCalendarList = async () => {
    if (pagination.offset !== 0 && pagination.offset >= pagination.count)
      return;

    try {
      setLoading(true);

      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
      };

      const response = await LeaveServices.getLeaveCalendar(params);

      if (response.status === 200) {
        // Merge and remove duplicates
        setLeaveCalendarList((prev) => {
          const merged = [...prev, ...response.data.results];
          const unique = Array.from(
            new Map(merged.map((item) => [item.id, item])).values()
          );
          return unique;
        });

        // NEW (append + remove duplicates)
        setEvents((prev) => {
          const merged = [
            ...prev,
            ...transformLeaveData(response.data.results),
          ];
          const unique = Array.from(
            new Map(merged.map((item) => [item.id, item])).values()
          );
          return unique;
        });

        // Pagination update
        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_FETCH_LEAVES
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Add Leave
  const addLeaveCalendarHandler = async (data) => {
    try {
      setActionLoading(true);

      const res = await LeaveServices.createLeave(data);

      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.ADD_LEAVE);
        setShowPopUpForm(false);

        // Append to table list
        setLeaveCalendarList((prev) => [...prev, res.data]);

        // Append to calendar events
        setEvents((prev) => [...prev, ...transformLeaveData([res.data])]);
      } else {
        const msg = extractErrorMessage(res) || TOAST_MESSAGES.FAILED_ADD_LEAVE;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || TOAST_MESSAGES.FAILED_ADD_LEAVE);
    } finally {
      setActionLoading(false);
      setShowPopUpForm(false);
    }
  };

  // Delete Leave
  const handleDelete = async (id) => {
    try {
      setActionLoading(true);

      const response = await LeaveServices.deleteLeave(id);

      if (response.status === 204) {
        toast.success(TOAST_MESSAGES.DELETE_LEAVE);

        // Remove from table list
        setLeaveCalendarList((prev) => prev.filter((item) => item.id !== id));

        // Remove from calendar events
        setEvents((prev) => prev.filter((event) => event.id !== id));
      } else {
        const msg = extractErrorMessage(response) || TOAST_MESSAGES.FAILED_DELETE_LEAVE;
        toast.error(msg);
      }
    } catch (error) {
      toast.error(
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_DELETE_LEAVE
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Render Table Fields
  const renderFields = (key, rowData) => {
    switch (key) {
      case "date":
        return formatDateDDMMYYYY(rowData.date);

      case "actions":
        return (
          <button
            onClick={() => handleDelete(rowData.id)}
            disabled={actionLoading || user.role !== ROLES.HR}
            className={`${
              actionLoading || user.role !== ROLES.HR
                ? "text-gray-500 cursor-not-allowed"
                : "text-red-500 hover:text-red-700"
            } text-xl p-2 rounded-lg hover:bg-red-50 transition-all disabled:opacity-50`}
          >
            <FiTrash2 />
          </button>
        );

      default:
        return rowData[key];
    }
  };

  // Popup Form
    const renderAddLeavePopup = () => (
    <PopupForm
        formData={addLeaveCalendarForm}
        isOpen={showPopUpForm}
        onClose={() => setShowPopUpForm(false)}
        handleData={(data) => addLeaveCalendarHandler(data)}
        loading={actionLoading}
        renderField={(key, formik) => {
            switch (key) {
            case "date":
                return (
                <div className="flex flex-col mb-4">
                    <label className="text-gray-700 mb-1">Date {" "}<sub className="text-red-500">*</sub></label>
                    <input
                    type="date"
                    name="date"
                    className="border rounded-lg px-3 py-2"
                    onChange={formik.handleChange}
                    value={formik.values.date}
                    />
                </div>
                );

            case "description":
                return (
                  <div className="flex flex-col mb-4">
                    <label className="text-gray-700 mb-1">
                      Description <sub className="text-red-500">*</sub>
                    </label>
                    <input
                      type="text"
                      name="description"
                      className="border rounded-lg px-3 py-2"
                      onChange={formik.handleChange}
                      value={formik.values.description}
                    />
                  </div>
                );

            case "note":
                return (
                  <div className="flex flex-col mb-4">
                    <label className="text-gray-700 mb-1">
                      Note <sub className="text-red-500">*</sub>
                    </label>
                    <textarea
                      name="note"
                      className="border rounded-lg px-3 py-2"
                      rows={3}
                      onChange={formik.handleChange}
                      value={formik.values.note}
                    />
                  </div>
                );

            default:
                return null;
            }
        }}
    />
    );

  // Pagination Reset Fetch
  useEffect(() => {
    getLeaveCalendarList();
  }, [isPaginationReset]);

  return (
    <div className="px-6 py-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-primary">Leave Calendar</h1>

        {(user.role === ROLES.HR || user.role === ROLES.DIRECTOR_MANAGEMENT) && (
          <button
            className="px-4 py-2 bg-primary text-white rounded-lg shadow-md hover:bg-green-700 transition"
            onClick={() => setShowPopUpForm(true)}
          >
            + Add Leave
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Calendar */}
        <div className="w-full lg:w-1/2 bg-white shadow-md rounded-2xl p-4 border border-gray-200">
          <Calendar events={events} />
        </div>

        <div className="block md:hidden text-xl text-primary font-medium">
          Leave List
        </div>
        {/* Desktop Table */}
        <div className="hidden lg:block w-full lg:w-1/2 bg-white shadow-md border border-gray-200">
          <div id="desktopScrollDiv" className="h-[65vh] overflow-y-auto pr-2">
            <InfiniteScroll
              dataLength={leaveCalendarList.length}
              next={getLeaveCalendarList}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="desktopScrollDiv"
              loader={
                <p className="text-center py-3 text-gray-500">Loading...</p>
              }
            >
              <Common.CommonTable
                tableColumns={LEAVE_CALENDAR_TABLE_COLUMNS}
                tableRows={leaveCalendarList}
                columnLabels={LEAVE_CALENDAR_COLUMN_LABELS}
                renderField={renderFields}
              />
            </InfiniteScroll>
          </div>
        </div>

        {/* Mobile Card View */}
        <div
          className="lg:hidden w-full h-[65vh] overflow-y-auto pr-2 mb-4"
          id="mobileScrollDiv"
        >
          <InfiniteScroll
            dataLength={leaveCalendarList.length}
            next={getLeaveCalendarList}
            hasMore={pagination.offset < pagination.count}
            loader={
              <p className="text-center py-3 text-gray-500">Loading...</p>
            }
            scrollableTarget="mobileScrollDiv" // ✅ now the div exists outside InfiniteScroll
          >
            <div className="flex flex-col gap-4">
              {leaveCalendarList.map((leave) => (
                <div
                  key={leave.id}
                  className="bg-white shadow-md rounded-xl p-4 border border-gray-200 flex flex-col gap-2"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="text-base font-medium text-gray-800">
                        {formatDateDDMMYYYY(leave.date)}
                      </p>
                    </div>

                    <button
                      onClick={() => handleDelete(leave.id)}
                      disabled={actionLoading || user.role !== ROLES.HR}
                      className={`${
                        actionLoading || user.role !== ROLES.HR
                          ? "text-gray-500 cursor-not-allowed"
                          : "text-red-500 hover:text-red-700"
                      } text-xl p-2 rounded-lg hover:bg-red-50 transition-all disabled:opacity-50`}
                    >
                      <FiTrash2 />
                    </button>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Description</p>
                    <p className="text-base text-gray-700 line-clamp-2">
                      {leave.description || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">Note</p>
                    <p className="text-base text-gray-700 line-clamp-3">
                      {leave.note || "-"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </InfiniteScroll>
        </div>

        {showPopUpForm && renderAddLeavePopup()}
      </div>
    </div>
  );
};

export default LeaveCalendar;
